/**
 * ForwardModal — reseller-side "send selected cards to MAX/Telegram".
 *
 * Two paths in:
 *   - "selected": user ticked individual cards in the feed
 *   - "all-filtered": user clicked "Forward all NN filtered"
 *
 * On open we fetch the user's reseller_settings (FX rate, caption
 * template, forward_targets) and render:
 *   1. A "you have N posts to forward" summary
 *   2. A live caption preview rendered from the template + the first
 *      post's data + the user's FX rate
 *   3. Target checkboxes — the user picks which of their configured
 *      targets receive this batch
 *   4. Send button → POST /api/forwards → shows per-post per-target
 *      success/failure on completion
 *
 * If the user has no FX rate or no targets configured, we surface a
 * "Set up forwarding first" CTA that links to the settings tab.
 */
import { useState, useEffect, useMemo } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Mono, Serif } from "./ui.jsx";
import { useT, plural } from "../i18n.jsx";

// Mirrors the server-side renderer in routes/forwards.ts. Kept in sync
// manually for now; if the variable set grows we should publish it from
// the API instead.
function renderCaption(template, vars) {
  return template.replace(/\{\{(\w+)\}\}/g, (m, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : m,
  );
}

function buildVars(post, fxRate) {
  const priceUsd = (post.priceCents ?? 0) / 100;
  const priceRub = priceUsd * fxRate;
  const msrpUsd = (post.originalPriceCents ?? 0) / 100;
  const msrpRub = msrpUsd * fxRate;
  const discountPct = post.discountPct ?? 0;
  return {
    // Localized title to match the (Russian-first) description below, so the
    // caption isn't a Russian body under an English product name.
    title: (post.titleRu || post.titleEn || post.titleEs || post.title || "").trim(),
    brand: post.brand || "",
    description: (post.bodyRu || post.bodyEn || post.bodyEs || "").trim(),
    price_usd: priceUsd.toFixed(2),
    price_rub: Math.round(priceRub).toLocaleString("ru-RU"),
    msrp_usd: msrpUsd.toFixed(2),
    msrp_rub: Math.round(msrpRub).toLocaleString("ru-RU"),
    discount_pct: String(discountPct),
    direct_link: post.sourceUrl || "",
    public_code: post.publicCode || "",
  };
}


export function ForwardModal({ mode, postIds, posts, onClose, onSent, onOpenForwardSettings, toast }) {
  const t = useT();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [pickedTargetIds, setPickedTargetIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.getResellerSettings();
        if (cancelled) return;
        setSettings(s);
        // Pre-tick all enabled targets by default — common case is
        // "send to my one channel". User can untick if they want.
        setPickedTargetIds(new Set(
          (s.forwardTargets ?? []).filter((t) => t.enabled).map((t) => t.id),
        ));
      } catch (e) {
        toast?.error?.(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Preview from the first post in the batch (so the reseller sees a
  // realistic example, not an empty placeholder).
  const previewPost = useMemo(() => {
    const id = postIds[0];
    return posts.find((p) => p.id === id) || null;
  }, [postIds, posts]);
  const previewCaption = useMemo(() => {
    if (!settings || !previewPost) return "";
    const vars = buildVars(previewPost, settings.fxRateRubPerUsd || 0);
    let body = renderCaption(settings.captionTemplate || "", vars);
    // Append the reseller's custom text just like the server does —
    // see renderCaption + appendCustom in apps/api/src/routes/forwards.ts.
    const custom = (settings.captionCustomText || "").trim();
    if (custom) body = `${body}\n\n${renderCaption(custom, vars)}`.trim();
    return body;
  }, [settings, previewPost]);

  const togglePick = (id) =>
    setPickedTargetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const canSend =
    !sending &&
    !loading &&
    settings &&
    settings.fxRateRubPerUsd > 0 &&
    pickedTargetIds.size > 0 &&
    postIds.length > 0;

  const send = async () => {
    setSending(true);
    try {
      const r = await api.createForward({
        postIds,
        targetIds: Array.from(pickedTargetIds),
      });
      setResults(r);
      // Don't auto-close — the reseller wants to see per-post results,
      // especially if some failed. We stay on the results view; user
      // closes when ready.
      onSent?.(r);
    } catch (e) {
      toast?.error?.(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20, 17, 13, 0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 40, padding: 20,
      }}
    >
      <div style={{
        background: T_NEW.paperRaised,
        borderRadius: T_NEW.r3,
        maxWidth: 560, width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        padding: 24,
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 12 }}>
          <Serif size={T_NEW.displaySm} style={{ fontWeight: 500 }}>
            {t("forward.title", { n: postIds.length })} {t(plural(postIds.length, "forward.cards"))}
          </Serif>
          <button
            onClick={onClose}
            aria-label={t("forward.cancel")}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T_NEW.ink56, fontSize: 24, lineHeight: 1, padding: 4,
            }}
          >×</button>
        </div>

        {mode === "all-filtered" && (
          <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 12 }}>
            {t("forward.allFilteredNote")}
          </Mono>
        )}

        {loading && (
          <p style={{ color: T_NEW.ink56 }}>{t("forward.loadingSettings")}</p>
        )}

        {/* Empty-state hints when prerequisites aren't configured.
            Each one ships with a direct "open Settings → Forward" CTA
            so the reseller doesn't have to hunt for the right tab —
            the parent passes onOpenForwardSettings which closes this
            modal and opens the settings modal pinned to the forward
            tab. Falls back gracefully (no button) if the caller didn't
            wire the prop. */}
        {!loading && settings && settings.fxRateRubPerUsd <= 0 && (
          <SetupHint
            title={t("forward.hint.noFx.title")}
            body={t("forward.hint.noFx.body")}
            actionLabel={t("forward.hint.openSettings")}
            onAction={onOpenForwardSettings && (() => { onClose(); onOpenForwardSettings(); })}
          />
        )}

        {!loading && settings && settings.fxRateRubPerUsd > 0 &&
         (!settings.forwardTargets || settings.forwardTargets.length === 0) && (
          <SetupHint
            title={t("forward.hint.noTarget.title")}
            body={t("forward.hint.noTarget.body")}
            actionLabel={t("forward.hint.openSettings")}
            onAction={onOpenForwardSettings && (() => { onClose(); onOpenForwardSettings(); })}
          />
        )}

        {/* Edge case: targets exist but none are enabled. The picker
            below would render an empty list and look broken; surface
            it as a fixable problem instead. */}
        {!loading && settings && settings.fxRateRubPerUsd > 0 &&
         (settings.forwardTargets?.length ?? 0) > 0 &&
         !settings.forwardTargets.some((tg) => tg.enabled) && (
          <SetupHint
            title={t("forward.hint.allDisabled.title")}
            body={t("forward.hint.allDisabled.body")}
            actionLabel={t("forward.hint.openSettings")}
            onAction={onOpenForwardSettings && (() => { onClose(); onOpenForwardSettings(); })}
          />
        )}

        {!loading && settings &&
         settings.fxRateRubPerUsd > 0 &&
         (settings.forwardTargets?.length ?? 0) > 0 && !results && (
          <>
            {/* Live preview — exactly what the audience will see in
                Telegram: card image on top, caption underneath, in a
                bubble-style container. We use the regular post image
                here (which is what Free-tier sends). Premium accounts
                strip the $-price/MSRP overlay on the actual send via
                the forward_image_path swap server-side, so the live
                preview is a faithful Free-tier preview and a slight
                over-estimate for Premium — acceptable. */}
            <div style={{ marginBottom: 18 }}>
              <Mono color={T_NEW.ink56}
                    style={{ display: "block", marginBottom: 6 }}>
                {t("forward.preview.label")}
              </Mono>
              <div style={{
                background: T_NEW.paperRaised,
                border: `1px solid ${T_NEW.ink12}`,
                borderRadius: T_NEW.r2,
                overflow: "hidden",
                boxShadow: T_NEW.shadowCard,
                maxWidth: 360,
                margin: "0 auto",
              }}>
                {(previewPost?.images?.length || 0) > 1 ? (
                  // Multi-photo posts forward as a Telegram album — preview the
                  // 2×2 collage so the reseller sees all photos that will be sent.
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2,
                                background: T_NEW.paperSoft }}>
                    {previewPost.images.slice(0, 4).map((img) => (
                      <img key={img.id || img.url} src={img.url} alt=""
                           style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" }} />
                    ))}
                  </div>
                ) : previewPost?.images?.[0]?.url ? (
                  <img src={previewPost.images[0].url} alt=""
                       style={{ width: "100%", height: "auto",
                                display: "block",
                                background: T_NEW.paperSoft }} />
                ) : (
                  <div style={{ width: "100%", aspectRatio: "1 / 1",
                                background: T_NEW.paperSoft,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: T_NEW.ink38,
                                fontSize: T_NEW.micro }}>
                    {t("forward.preview.noImage")}
                  </div>
                )}
                <pre style={{
                  margin: 0,
                  padding: "10px 12px 12px",
                  fontFamily: T_NEW.fontSans,
                  fontSize: T_NEW.body,
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  color: T_NEW.ink,
                  lineHeight: 1.5,
                  background: T_NEW.paperRaised,
                  borderTop: `1px solid ${T_NEW.ink12}`,
                }}>
                  {previewCaption || t("forward.preview.emptyCaption")}
                </pre>
              </div>
              {/* Footer meta — FX rate + batch size hint. Frames the
                  preview so the reseller knows the price came from
                  their own settings. */}
              <Mono color={T_NEW.ink38} size={T_NEW.micro}
                    style={{ display: "block", marginTop: 8,
                             textAlign: "center" }}>
                {t("forward.preview.rate", { rate: settings.fxRateRubPerUsd.toFixed(2) })}
                {postIds.length > 1 && t("forward.preview.batchSize", { n: postIds.length })}
              </Mono>
            </div>

            {/* Target picker */}
            <div style={{ marginBottom: 18 }}>
              <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
                {t("forward.target.label")}
              </Mono>
              {settings.forwardTargets.filter((tg) => tg.enabled).map((tg) => (
                <label
                  key={tg.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: T_NEW.r1,
                    background: pickedTargetIds.has(tg.id) ? T_NEW.persimmonSoft : "transparent",
                    cursor: "pointer", marginBottom: 4,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={pickedTargetIds.has(tg.id)}
                    onChange={() => togglePick(tg.id)}
                  />
                  <span style={{ fontSize: T_NEW.body }}>
                    {/* Friendly label if set, otherwise just the chat ID —
                        that's the canonical identifier and most resellers
                        won't bother adding a nickname. */}
                    {tg.label?.trim() || tg.chatId}
                  </span>
                  <Mono color={T_NEW.ink56} size={T_NEW.micro}>
                    {tg.kind === "telegram" ? t("forward.target.kind.tg") : t("forward.target.kind.max")}
                    {tg.label?.trim() && ` · ${tg.chatId}`}
                  </Mono>
                  {tg.kind === "max" && (
                    <Mono color={T_NEW.persimmon} size={T_NEW.micro} style={{ marginLeft: "auto" }}>
                      {t("forward.target.soon")}
                    </Mono>
                  )}
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={onClose} disabled={sending}>
                {t("forward.cancel")}
              </Btn>
              <Btn
                variant="accent"
                disabled={!canSend}
                onClick={send}
              >
                {sending ? t("forward.sending") : t("forward.send", { n: postIds.length })}
              </Btn>
            </div>
          </>
        )}

        {results && (
          <ResultSummary results={results} onClose={onClose} t={t} />
        )}
      </div>
    </div>
  );
}


// ─── Helpers ─────────────────────────────────────────────────────────
// Persimmon-tinted hint card used to surface missing forward prereqs
// (FX rate, target channel, all-targets-disabled). When `onAction` is
// provided we render a small inline button under the body — saves the
// reseller from closing this modal and hunting for the right settings
// tab themselves.
function SetupHint({ title, body, actionLabel, onAction }) {
  return (
    <div style={{
      background: T_NEW.persimmonSoft,
      color: T_NEW.persimmon,
      padding: "12px 14px",
      borderRadius: T_NEW.r1,
      marginBottom: 12,
    }}>
      <strong style={{ display: "block", marginBottom: 4 }}>{title}</strong>
      <span style={{ fontSize: T_NEW.meta, color: T_NEW.ink, opacity: 0.85,
                     display: "block" }}>
        {body}
      </span>
      {onAction && (
        <button
          onClick={onAction}
          style={{
            marginTop: 10,
            background: T_NEW.persimmon,
            color: T_NEW.paperOnInk,
            border: "none",
            borderRadius: T_NEW.r1,
            padding: "7px 14px",
            fontSize: T_NEW.micro,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: T_NEW.fontMono,
            textTransform: "uppercase",
            letterSpacing: T_NEW.monoTrack,
          }}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}


function ResultSummary({ results, onClose, t }) {
  const { okCount = 0, totalCount = 0, status = "completed" } = results;
  const banner =
    status === "failed"
      ? { color: T_NEW.persimmon, bg: T_NEW.persimmonSoft, msg: t("forward.result.failed") }
      : okCount === totalCount
        ? { color: T_NEW.ink, bg: T_NEW.paperSoft, msg: t("forward.result.allOk", { n: okCount }) }
        : { color: T_NEW.persimmon, bg: T_NEW.persimmonSoft, msg: t("forward.result.partial", { ok: okCount, total: totalCount }) };
  return (
    <>
      <div style={{
        background: banner.bg, color: banner.color,
        padding: "12px 14px", borderRadius: T_NEW.r1, marginBottom: 12,
        fontWeight: 500,
      }}>
        {banner.msg}
      </div>
      {/* Show only the failures — successes don't need attention */}
      <FailureList results={results.results || {}} t={t} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <Btn variant="accent" onClick={onClose}>{t("forward.result.done")}</Btn>
      </div>
    </>
  );
}


function FailureList({ results, t }) {
  const failures = [];
  for (const [postId, perTarget] of Object.entries(results)) {
    for (const [targetId, r] of Object.entries(perTarget)) {
      if (!r?.ok) failures.push({ postId, targetId, error: r?.error || "?" });
    }
  }
  if (failures.length === 0) return null;
  return (
    <div style={{
      maxHeight: 200, overflowY: "auto",
      border: `1px solid ${T_NEW.ink12}`,
      borderRadius: T_NEW.r1,
      padding: 8, background: T_NEW.paperSoft,
      fontSize: T_NEW.meta,
    }}>
      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
        {t("forward.result.notSent", { n: failures.length })}
      </Mono>
      {failures.slice(0, 12).map((f, i) => (
        <div key={i} style={{ marginBottom: 4, color: T_NEW.ink }}>
          <code style={{ color: T_NEW.ink56, fontSize: T_NEW.micro }}>{f.postId.slice(0, 8)}</code>
          {" → "}{f.error}
        </div>
      ))}
      {failures.length > 12 && (
        <Mono color={T_NEW.ink38} size={T_NEW.micro} style={{ display: "block", marginTop: 4 }}>
          {t("forward.result.andMore", { n: failures.length - 12 })}
        </Mono>
      )}
    </div>
  );
}
